'use client';

import { createContext, useContext, useEffect, useState } from 'react';

interface AuthUser {
  id: string;
  email: string;
  full_name: string;
  phone: string;
  address: string;
  avatar_url: string;
  created_at?: string;
}

interface AuthContextValue {
  user: AuthUser | null;
  session: AuthUser | null;
  loading: boolean;
  signUp: (
    email: string,
    password: string,
    metadata?: { fullName?: string; phone?: string }
  ) => Promise<AuthUser>;
  signIn: (email: string, password: string) => Promise<AuthUser>;
  signOut: () => Promise<void>;
  getCurrentUser: () => Promise<AuthUser | null>;
  isEmailVerified: () => boolean;
  getUserProfile: () => Promise<AuthUser | null>;
  refreshUser: () => Promise<AuthUser | null>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

async function request<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    ...init,
    cache: 'no-store',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers || {}),
    },
  });

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || 'Request failed');
  }

  return data;
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshUser = async () => {
    try {
      const data = await request<{ user: AuthUser | null }>('/api/auth/me');
      setUser(data.user);
      return data.user;
    } catch {
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshUser();
  }, []);

  const signUp = async (
    email: string,
    password: string,
    metadata: { fullName?: string; phone?: string } = {}
  ) => {
    const data = await request<{ user: AuthUser }>('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify({
        email,
        password,
        fullName: metadata.fullName || '',
        phone: metadata.phone || '',
      }),
    });
    setUser(data.user);
    return data.user;
  };

  const signIn = async (email: string, password: string) => {
    const data = await request<{ user: AuthUser }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    setUser(data.user);
    return data.user;
  };

  const signOut = async () => {
    await request('/api/auth/logout', { method: 'POST' });
    setUser(null);
  };

  const getCurrentUser = async () => {
    return refreshUser();
  };

  const isEmailVerified = () => {
    return Boolean(user?.email);
  };

  const getUserProfile = async () => {
    const data = await request<{ profile: AuthUser }>('/api/account/profile');
    return data.profile;
  };

  const value: AuthContextValue = {
    user,
    session: user,
    loading,
    signUp,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
